/**
 * **主区域的滚动位置：报上去 + 恢复回来** —— 从 `layout/NpArea.vue`
 * 向下拆出来的一小块（约 30 行）。
 *
 * ## 它在解决什么
 *
 * 换编辑器 / 换 Area 内容之后，主区域里的容器会**重建**，滚动位置就丢了。
 * 所以两头各做一件事：
 *
 * ```text
 *   滚动时  把「哪个容器停在哪」报给宿主   （onMainScroll → emit('scroll')）
 *   标记变了 把位置贴回去                   （restoreScroll 读 props.scrollMark）
 * ```
 *
 * 「用哪个选择器认这个容器」是**共享的约定**，在
 * `layout/viewportMemory.ts` 的 `scrollSelectorFor`（按容器的类名推）。
 * 报上去和贴回来必须用同一套 —— 否则会出现「报的是 A、找的是 B」，
 * 表现就是「滚动位置偶尔恢复不了」。
 *
 * ## ⚠️ 一条**踩过坑才写对**的规矩
 *
 * **恢复必须放在 `nextTick` 之后。** 面板内容是挂载后才渲染出来的，
 * 早于它 `querySelector` 找不到锚点（换编辑器之后尤其明显）。
 *
 * ## 归属
 *
 * **向下**：`NpArea.vue` → `composables/useScrollMemory.ts`，只有前者引用它。
 *
 * ⚠️ **函数体逐字不动**（这个仓库的搬运规矩）。
 */
import { nextTick, onMounted, watch } from 'vue'
import { scrollSelectorFor } from '../viewportMemory'

/** 宿主存下来的「某个容器停在哪」 */
export interface ScrollMark {
  selector: string
  top: number
  left: number
}

export interface ScrollMemoryCtx {
  /** 主区域元素（在里面 `querySelector` 找锚点） */
  bodyEl: () => HTMLElement | null
  /** 要恢复到的位置（宿主传下来的标记） */
  scrollMark: () => ScrollMark | null | undefined
  /** 滚动时报上去 */
  scrolled: (mark: ScrollMark) => void
}

export function useScrollMemory(ctx: ScrollMemoryCtx) {
  /** 主区域滚动 —— 把「哪个容器停在哪」报给宿主 */
  function onMainScroll(ev: Event) {
    const el = ev.target as HTMLElement | null
    if (!el || typeof el.scrollTop !== 'number') return
    const selector = scrollSelectorFor(Array.from(el.classList ?? []))
    if (!selector) return
    ctx.scrolled({ selector, top: el.scrollTop, left: el.scrollLeft })
  }

  /**
   * 恢复滚动位置。
   *
   * ⚠️ 放在 `nextTick` 之后：面板内容是挂载后才渲染出来的，
   * 早于它 `querySelector` 找不到锚点（换编辑器之后尤其明显）。
   */
  function restoreScroll() {
    const mark = ctx.scrollMark()
    const host = ctx.bodyEl()
    if (!mark || !host) return
    const target = host.querySelector(mark.selector) as HTMLElement | null
    if (!target) return
    target.scrollTop = mark.top
    target.scrollLeft = mark.left
  }

  onMounted(() => nextTick(restoreScroll))
  /*
   * 换编辑器 / 换 Area 内容之后主区域里的容器会重建，
   * 所以 `scrollMark` 一变就再恢复一次。
   */
  watch(() => ctx.scrollMark(), () => nextTick(restoreScroll))

  return { onMainScroll, restoreScroll }
}
